import {useState, useCallback, useRef} from 'react'
import {useServices} from '@/services/context'

interface DuplicateCheckState {
    checking: boolean
    exists: boolean
    error: string | null
}

const DEBOUNCE_MS = 300
const INITIAL_STATE: DuplicateCheckState = {checking: false, exists: false, error: null}

/**
 * Generic debounced duplicate check helper.
 * Manages debounce timer, loading state, and result state.
 */
function useDebouncedCheck<TArgs extends unknown[]>(
    checkFn: (...args: TArgs) => Promise<{exists: boolean}>,
    isEmpty: (...args: TArgs) => boolean
) {
    const [state, setState] = useState<DuplicateCheckState>(INITIAL_STATE)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const check = useCallback((...args: TArgs) => {
        if (timerRef.current) clearTimeout(timerRef.current)

        if (isEmpty(...args)) {
            setState(INITIAL_STATE)
            return
        }

        setState(prev => ({...prev, checking: true}))
        timerRef.current = setTimeout(async () => {
            try {
                const result = await checkFn(...args)
                setState({checking: false, exists: result.exists, error: null})
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err)
                setState({checking: false, exists: false, error: message})
            }
        }, DEBOUNCE_MS)
    }, [checkFn, isEmpty])

    const reset = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        setState(INITIAL_STATE)
    }, [])

    return {...state, check, reset}
}

/**
 * Debounced duplicate check hook for party display names.
 * Calls the validation API on blur with 300ms debounce.
 */
export function useDuplicateNameCheck() {
    const services = useServices()
    const checkFn = useCallback(
        (displayName: string) => services.onboarding.checkPartyName(displayName.trim()),
        [services]
    )
    const isEmpty = useCallback((displayName: string) => !displayName.trim(), [])

    return useDebouncedCheck(checkFn, isEmpty)
}

/**
 * Debounced identifier duplicate check hook.
 */
export function useDuplicateIdentifierCheck() {
    const services = useServices()
    const checkFn = useCallback(
        (identifierType: string, identifierValue: string) =>
            services.onboarding.checkIdentifier(identifierType, identifierValue.trim()),
        [services]
    )
    const isEmpty = useCallback(
        (_identifierType: string, identifierValue: string) => !identifierValue.trim(),
        []
    )

    return useDebouncedCheck(checkFn, isEmpty)
}
